import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * Custom Google OAuth landing page — Authorization Code return.
 *
 * Google redirects the user back to `/auth/google?code=…&state=…`.
 * We:
 *   1. CSRF-check `state` against the value we stashed in
 *      sessionStorage right before the redirect.
 *   2. POST the code to /api/auth/google/oauth with the EXACT same
 *      redirect_uri Google saw — `${origin}/auth/google` — or the
 *      token exchange will fail with redirect_uri_mismatch.
 *   3. Store the returned JWT and route based on onboarding state.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthGoogle() {
  const navigate = useNavigate();
  const { setSessionFromOAuth } = useAuth();
  const { t } = useTranslation();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const code   = params.get("code");
    const state  = params.get("state");
    const oauthError = params.get("error");

    if (oauthError) {
      toast.error(
        oauthError === "access_denied"
          ? "ההתחברות בוטלה"
          : `שגיאת התחברות: ${oauthError}`,
      );
      navigate("/auth", { replace: true });
      return;
    }

    if (!code) {
      // Direct visit to /auth/google with no code — silent bail-out
      // so we don't show a spurious error toast.
      return;
    }

    // CSRF — must match the random state we set before redirecting.
    const expectedState = sessionStorage.getItem("google_oauth_state");
    sessionStorage.removeItem("google_oauth_state");
    if (!expectedState || expectedState !== state) {
      toast.error("בקשת ההתחברות פגה. נסה שוב.");
      navigate("/auth", { replace: true });
      return;
    }

    // Strip query so a back-button or refresh can't replay the code.
    try { window.history.replaceState({}, "", "/auth/google"); } catch {}

    (async () => {
      try {
        const res = await setSessionFromOAuth({
          code,
          // REMINDER: DO NOT HARDCODE — must match the URI Google saw
          // (which itself was built from window.location.origin).
          redirect_uri: `${window.location.origin}/auth/google`,
        });
        toast.success(
          res?.is_new_user
            ? t("auth.toast_register_success")
            : t("auth.toast_login_success"),
        );
        const needsOnboarding =
          res?.is_new_user || res?.user?.onboarding_completed === false;
        navigate(needsOnboarding ? "/onboarding" : "/beit-midrash", {
          replace: true,
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("auth.toast_google_signin_failed"));
        navigate("/auth", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-[60vh] flex items-center justify-center"
      data-testid="auth-google-callback"
    >
      <div className="text-center space-y-4">
        <div className="h-10 w-10 mx-auto rounded-full border-2 border-gold/30 border-t-gold animate-spin" />
        <p className="font-heading text-lg text-foreground">מתחבר ל-Google...</p>
      </div>
    </div>
  );
}
